import * as ko from 'knockout';

import { listTraitActionOptions, TraitActionOptionsData } from '../../api/trait_actions';
import { SelectedTrait } from '../../models/trial';
import { TraitAction } from '../../models/trait_action';

export abstract class BaseTraitActionViewModel {
  isEnabled = ko.observable(true);
  switchOn = ko.observable(false);
  loadingTraitActionOptions = ko.observable(true);
  loadingNewTraitAction = ko.observable(false);

  selectedTraitIds: ko.PureComputed<string[]>;
  traitActions: ko.ObservableArray<TraitAction>;
  validationError: ko.PureComputed<string>;

  protected subscriptions: KnockoutSubscription[] = [];

  constructor(params: {
    initialTraitActions: ko.ObservableArray<TraitAction>;
    selectedTraits: ko.ObservableArray<SelectedTrait>;
    traitActionsError?: ko.Observable<string>;
  }) {
    this.selectedTraitIds = ko.pureComputed(() =>
      params
        .selectedTraits()
        .filter((trait) => this._shouldIncludeTrait(trait, params.initialTraitActions()))
        .map((trait) => trait.mmId)
    );
    this.traitActions = params.initialTraitActions;
    
    // We expect the subclasses to call refreshTraitActionOptions & setupTraitActions
    // depending on peek vs observe, though we could do it here:
    this.refreshTraitActionOptions(params.initialTraitActions());
    this.setupTraitActions(params.initialTraitActions.peek());

    this.validationError = ko.pureComputed(() => {
      if (params.traitActionsError) {
        return params.traitActionsError() || '';
      }
      return (this.traitActions.error && this.traitActions.error()) || '';
    });

    this.subscriptions.push(
      this.isEnabled.subscribe(
        (curValue) => {
          if (curValue) {
            // Turn off switch before disabling it
            this.switchOn(false);
          }
        },
        this,
        'beforeChange'
      ),
      this.switchOn.subscribe((isSwitchedOn) => {
        if (!isSwitchedOn) {
          if (this.traitActions().length > 0) {
            this.traitActions.removeAll();
          }
        } else {
          if (this.traitActions().length === 0) {
            this.addNewAction();
          }
        }
      }),
      this.selectedTraitIds.subscribe(() => {
        this.refreshTraitActionOptions(this.traitActions());
      }),
      this.traitActions.subscribe((newTraitActions) => {
        const hasActions = newTraitActions.length > 0;
        if (hasActions && !this.switchOn()) {
          this.switchOn(true);
        } else if (!hasActions && this.switchOn()) {
          this.switchOn(false);
        }
      })
    );
  }

  _shouldIncludeTrait(trait: SelectedTrait, initialTraitActions: TraitAction[]) {
    return (
      trait.isSaved ||
      initialTraitActions.some((action) => action.sourceTraitId() === trait.mmId) ||
      initialTraitActions.some((action) => action.targetTraitId() === trait.mmId)
    );
  }

  protected fetchTraitOptions(selectedTraitIds: string[]) {
    return listTraitActionOptions({ measurement_meta_ids: selectedTraitIds });
  }

  protected async refreshTraitActionOptions(traitActions: TraitAction[]) {
    try {
      this.loadingTraitActionOptions(true);
      const options = await this.fetchTraitOptions(this.selectedTraitIds());
      traitActions.forEach((traitAction) => {
        traitAction.setOptions(options);
        this.onTraitOptionsRefreshed(traitAction, options);
      });
      this.updateIsEnabled(options);
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingTraitActionOptions(false);
    }
  }

  protected onTraitOptionsRefreshed(traitAction: TraitAction, options: TraitActionOptionsData) {
    // optional hook for subclasses
  }

  protected updateIsEnabled(options: TraitActionOptionsData) {
    this.isEnabled(options.sources.length > 0 && options.targets.length > 0);
  }

  protected setupTraitActions(initialTraitActions: TraitAction[]) {
    this.switchOn(initialTraitActions.length > 0);
  }

  addNewAction() {
    const newTraitAction = new TraitAction();
    this.onAddNewAction(newTraitAction);
    
    this.loadingNewTraitAction(true);
    this.fetchTraitOptions(this.selectedTraitIds())
      .then((options) => {
        this.traitActions.push(newTraitAction);
        newTraitAction.setOptions(options);
      })
      .finally(() => {
        this.loadingNewTraitAction(false);
      });
  }

  protected onAddNewAction(newTraitAction: TraitAction) {
    // override to init type
  }

  removeAction(traitAction: TraitAction) {
    this.traitActions.remove(traitAction);
  }

  dispose() {
    this.subscriptions.forEach((sub) => sub.dispose());
    this.subscriptions = [];
  }
}
